// ════════════════════════════════════════════════════════════════════════════
//  ManDana — what a manifestation has been allowed to do
//  ---------------------------------------------------------------------------
//  A permission model for code somebody else wrote, running over somebody's
//  journal. It is deliberately shaped like a browser permission: asked once, in
//  THIS app's own chrome and never inside the frame, remembered, and revocable
//  from the card's own menu.
//
//  ── THREE LEVELS, AND ONE OF THEM IS NOT A LEVEL ──────────────────────────
//  none / read / write, and `write` implies `read` because a dashboard that
//  edits a record must first be able to show it. Asking twice for one idea is
//  how a permission prompt becomes a thing people click through.
//
//  Ops marked `own` in md-bridge.ts's table need no grant at all. A manifestation
//  reading and writing its OWN store is not touching anything anybody else can
//  see, and there is nothing there to consent to.
//
//  ── KEYED ON THE INSTANCE, VERIFIED ON THE BYTES ──────────────────────────
//  The key is the instance id, minted from crypto.getRandomValues. It is NOT
//  md-file.ts's content hash: that is FNV-1a xor djb2, cheap enough to collide
//  on purpose, and its own comment already warns that a collision means one
//  document silently becoming another. Survivable for a PDF; not survivable for
//  the key to a permission to write a journal, because a grant keyed on a cheap
//  hash is a grant an attacker can AIM a document at.
//
//  But the code still has to be the code that was allowed, so the grant carries
//  a SHA-256 of the bytes and a mismatch refuses everything and asks again.
//  Together those two give the property that matters in practice: drop a new
//  version onto the same portal and its DATA survives, because the instance is
//  the same, while its PERMISSION does not, because the code is not.
//
//  ── IT DOES NOT SYNC, AND THAT IS CORRECT ─────────────────────────────────
//  One local document, written through md-storage.js like everything else, and
//  never added to mdLocalDocs(). A grant that travelled would arrive on a device
//  that does not have the bytes — a permission for nothing, granted for code
//  that device has never seen. When manifestations learn to travel, this
//  travels with them and not before.
// ════════════════════════════════════════════════════════════════════════════

(function (g: any) {
  'use strict';

  const MD_GRANT_KEY = 'md:manifestGrants';

  /* Ordered, so "at least this much" is a comparison rather than a table of
     cases. `own` is absent on purpose — it is not a level somebody grants. */
  const LEVELS = ['none', 'read', 'write'];

  const DIGEST_RE = /^[0-9a-f]{64}$/;

  function all(): Record<string, { level: string; digest: string; reach?: unknown }> {
    const raw = g.mdRead ? g.mdRead(MD_GRANT_KEY, {}) : {};
    return raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : {};
  }

  /* Whether a stored `reach` is still a declaration set this app understands.
     md-manifest.ts owns that answer and is loaded by the time anybody grants
     anything — the same soft dependency mdGrantAllows already has on
     MD_BRIDGE_OPS. Absent validator means store nothing: a permission record
     is the wrong place to keep data whose shape we cannot currently check. */
  function reachOk(reach: unknown): boolean {
    return typeof g.mdCraftDeclarationsOk === 'function' && !!g.mdCraftDeclarationsOk(reach);
  }

  /** What this manifestation was allowed, the code it was allowed for, and —
      since 1D-1b — WHAT IT ASKED FOR AT THE TIME. The last of those is what
      makes "this version also wants your Suppliers table" a sentence the app
      can write on a re-grant, instead of asking the same faceless question
      twice. It is evidence, not configuration: nothing reads it to decide
      anything, it exists so a person can be told what changed. */
  function mdGrantRead(instance: unknown): { level: string; digest: string; reach?: unknown } {
    const none = { level: 'none', digest: '' };
    if (typeof instance !== 'string' || !instance) return none;
    const row = all()[instance];
    if (!row || typeof row !== 'object') return none;
    const level = LEVELS.indexOf(row.level) > 0 ? row.level : 'none';
    const digest = typeof row.digest === 'string' && DIGEST_RE.test(row.digest) ? row.digest : '';
    if (level === 'none') return none;
    return reachOk(row.reach) ? { level, digest, reach: row.reach } : { level, digest };
  }

  /** Remember an answer. Returns false rather than throwing when it cannot.

      `reach` is the declaration set the person was SHOWN — the caller must
      pass the one parsed from the digested bytes, never an entry's stored
      copy, for the reason md-manifest.ts's declarationsOf sets out at length.
      Omitting it is allowed and simply means this device cannot say later what
      changed; storing a malformed one is not, because a permissions panel that
      renders whatever was in storage is a place to put words in the app's
      mouth. */
  function mdGrantSet(instance: unknown, level: unknown, digest: unknown, reach?: unknown): boolean {
    if (typeof instance !== 'string' || !instance) return false;
    if (typeof level !== 'string' || LEVELS.indexOf(level) <= 0) return false;
    if (typeof digest !== 'string' || !DIGEST_RE.test(digest)) return false;
    const next = all();
    next[instance] = reachOk(reach) ? { level, digest, reach } : { level, digest };
    return g.mdWrite ? !!g.mdWrite(MD_GRANT_KEY, next) : false;
  }

  function mdGrantRevoke(instance: unknown): boolean {
    if (typeof instance !== 'string' || !instance) return false;
    const next = all();
    if (!next[instance]) return false;
    delete next[instance];
    return g.mdWrite ? !!g.mdWrite(MD_GRANT_KEY, next) : false;
  }

  /** The question the bridge asks before it does anything.

      `digest` is what the code IS right now, read from the bytes at load. The
      grant carries what it WAS when somebody said yes. */
  function mdGrantAllows(grant: unknown, op: unknown, digest: unknown): boolean {
    const table = g.MD_BRIDGE_OPS || {};
    if (typeof op !== 'string' || !Object.prototype.hasOwnProperty.call(table, op)) return false;
    const need = table[op];

    /* Its own store, before any question about grants — including before the
       digest check. Losing a manifestation's rows every time its code is edited
       would make a version bump destructive, and those rows were never anybody
       else's business to begin with. */
    if (need === 'own') return true;

    if (!grant || typeof grant !== 'object') return false;
    const gr = grant as { level?: unknown; digest?: unknown };
    const have = LEVELS.indexOf(typeof gr.level === 'string' ? gr.level : '');
    if (have <= 0) return false;

    /* The code changed under a permission granted to different code. Refuse
       everything and let the app ask again — silently carrying the grant over
       is the failure this whole design exists to prevent. */
    if (typeof digest !== 'string' || !digest || gr.digest !== digest) return false;

    return have >= LEVELS.indexOf(need);
  }

  /** A real digest of the bytes. crypto.subtle, not mdFileHash: this one is
      asked to survive somebody trying to make two documents agree. */
  async function mdGrantDigest(bytes: unknown): Promise<string> {
    const subtle = g.crypto && g.crypto.subtle;
    if (!subtle || !bytes) return '';
    try {
      const buf = await subtle.digest('SHA-256', bytes as ArrayBufferView);
      return Array.from(new Uint8Array(buf))
        .map((b) => (b as number).toString(16).padStart(2, '0')).join('');
    } catch (e) {
      return '';
    }
  }

  g.MD_GRANT_KEY = MD_GRANT_KEY;
  g.MD_GRANT_LEVELS = LEVELS;
  g.mdGrantRead = mdGrantRead;
  g.mdGrantSet = mdGrantSet;
  g.mdGrantRevoke = mdGrantRevoke;
  g.mdGrantAllows = mdGrantAllows;
  g.mdGrantDigest = mdGrantDigest;

  // CommonJS escape hatch, following md-bridge.ts and md-manifest.ts.
  const mod = (globalThis as { module?: { exports?: unknown } }).module;
  if (mod && mod.exports) {
    mod.exports = { MD_GRANT_KEY, MD_GRANT_LEVELS: LEVELS, mdGrantRead, mdGrantSet,
      mdGrantRevoke, mdGrantAllows, mdGrantDigest };
  }
})(typeof window !== 'undefined' ? window : globalThis);
